/**
 * Api — the OneTrust methods. index.js composes these onto ThryvApi.oneTrust.
 *
 * Usage is documented in index.js.
 */

/**
 * "Social Media Cookies" -> "social-media-cookies".
 *
 * DERIVED FROM THE NAME, because OneTrust has no slug field — a group carries an id and a display
 * name and nothing between them. So a renamed category changes its slug, while its id does not:
 * match on `id` for anything that must keep working, and treat the slug as a readable label.
 */
const slugify = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/** Flatten the argument forms — ("a"), ("a", "b") and (["a", "b"]) all mean the same thing. */
const terms = (args) =>
  args
    .flat()
    .map((term) => String(term).trim().toLowerCase())
    .filter(Boolean);

const Api = {
  /**
   * Has the banner loaded and the visitor's choice been resolved?
   *
   * `OnetrustActiveGroups` exists only once OneTrust has run, so every method here has to cope with
   * being called before that — see the note on optedIn().
   */
  ready: () => typeof window.OnetrustActiveGroups === "string",

  /**
   * Run a callback once consent is known, and again whenever the visitor changes it.
   *
   * WHY THIS EXISTS RATHER THAN EACH CALLER WRITING IT. OneTrust arrives late and by two different
   * routes here — GTM, and the legacy wrapper's own lazy loader — so a caller that simply asks at
   * DOMContentLoaded gets [] and reads it as "declined everything". The correct handling is the same
   * every time: act now if consent is already resolved, otherwise wait for OneTrust's event.
   *
   * FIRES AGAIN ON CHANGE, deliberately. `OneTrustGroupsUpdated` is dispatched when the visitor
   * reopens the preference centre and revises their choice, and a gate that only ran once would keep
   * acting on permission that has since been withdrawn.
   *
   *   ThryvApi.oneTrust.whenReady(() => {
   *     if (ThryvApi.oneTrust.optedIn("C0004")) loadAdPixel();
   *   });
   *
   * @param  {Function} callback
   * @return {Function}  removes the listener, for a caller that needs to stop listening
   */
  whenReady(callback) {
    if (typeof callback !== "function") return () => {};

    const handler = () => callback();

    // The event is the only signal for a change, so it is listened for either way — not just when
    // consent is still unknown.
    window.addEventListener("OneTrustGroupsUpdated", handler);

    if (this.ready()) callback();

    return () => window.removeEventListener("OneTrustGroupsUpdated", handler);
  },

  /**
   * What the visitor has ACCEPTED — the list, or a yes/no about specific categories.
   *
   *   optedIn()                            [{ id, slug, name }, …]
   *   optedIn("C0004")                     true / false
   *   optedIn("targeting-cookies")         same, matched by slug
   *   optedIn("C0002", "C0004")            true only if BOTH are accepted
   *   optedIn(["C0002", "C0004"])          same, as an array
   *
   * ALL, NOT ANY. A gate asks "may I do this", and doing something that needs two categories when
   * only one was granted is the failure that matters. Ask twice for an either/or.
   *
   * READ AT CALL TIME, NEVER CACHED. A visitor can reopen the preference centre at any point and
   * OneTrust rewrites the underlying global in place — a value captured at page load goes stale
   * silently, which with consent means acting on permission that was withdrawn.
   *
   * THE ID IS ALWAYS THERE; slug AND name MAY BE EMPTY. Ids come from `OnetrustActiveGroups`, which
   * OneTrust sets early; names come from `GetDomainData()`, only available once its full object is
   * up. So MATCHING BY ID WORKS SOONER THAN MATCHING BY SLUG — prefer ids for anything that gates.
   *
   * With no argument, returns [] before OneTrust has loaded at all: indistinguishable from "accepted
   * nothing", which is the safe reading of both.
   *
   * @param  {...(string|string[])} wanted
   * @return {Array<{id: string, slug: string, name: string}>|boolean}
   */
  optedIn(...wanted) {
    const named = this.categories();
    const accepted = this.acceptedIds().map((id) => {
      const match = named.find((group) => group.id === id);

      return { id, slug: match ? match.slug : "", name: match ? match.name : "" };
    });

    const asked = terms(wanted);

    if (asked.length === 0) return accepted;

    return asked.every((term) =>
      accepted.some((group) => group.id.toLowerCase() === term || group.slug === term),
    );
  },

  /**
   * What the visitor has DECLINED — the list, or a yes/no about specific categories.
   *
   *   optedOut()                           [{ id, slug, name }, …]
   *   optedOut("C0004")                    true / false
   *   optedOut("targeting-cookies")        same, matched by slug
   *   optedOut("C0002", "C0004")           true only if BOTH were declined
   *
   * A CATEGORY OneTrust DOES NOT HAVE IS NOT "DECLINED". Asking about an id that is not configured
   * on this domain returns false, not true — we cannot report a refusal of something the visitor was
   * never offered, and a typo in an id would otherwise read as a definite no.
   *
   * NEEDS THE FULL CATEGORY LIST, which only exists once OneTrust's own object is up. Until then
   * this returns [] (or false), because "we cannot tell yet" must not be reported as "they declined".
   *
   * @param  {...(string|string[])} wanted
   * @return {Array<{id: string, slug: string, name: string}>|boolean}
   */
  optedOut(...wanted) {
    const accepted = this.acceptedIds();

    // SAME SHAPE AS optedIn(). categories() carries an `accepted` flag, which is meaningful there
    // and noise here — every entry in this list is declined by definition, and a field saying so on
    // one of the two lists but not the other is a difference a caller has to look up.
    const declined = this.categories()
      .filter((group) => !accepted.includes(group.id))
      .map(({ id, slug, name }) => ({ id, slug, name }));

    const asked = terms(wanted);

    if (asked.length === 0) return declined;

    return asked.every((term) =>
      declined.some((group) => group.id.toLowerCase() === term || group.slug === term),
    );
  },

  /**
   * Just the accepted ids, from the one global OneTrust sets early.
   *
   * The fast path the others are built on: it answers as soon as `OnetrustActiveGroups` exists,
   * without waiting for the full OneTrust object that carries the names.
   *
   * @return {string[]}
   */
  acceptedIds() {
    // The global is a comma-delimited string with leading and trailing commas: ",C0001,C0003,".
    return String(window.OnetrustActiveGroups || "")
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  },

  /**
   * Every configured category — id, slug, name, and whether it is accepted.
   *
   * Ids alone ("C0003") mean nothing without OneTrust's own naming, which is configured per domain
   * rather than fixed, so the names come from OneTrust rather than a table here.
   *
   * @return {Array<{id: string, slug: string, name: string, accepted: boolean}>}  [] until loaded
   */
  categories() {
    const source = window.OneTrust || window.Optanon;

    if (!source || typeof source.GetDomainData !== "function") return [];

    // acceptedIds(), NOT optedIn() — optedIn() calls this method to attach names, so reaching back
    // for it here is mutual recursion and a blown stack.
    const accepted = this.acceptedIds();
    const groups = source.GetDomainData().Groups || [];

    return groups
      .map((group) => ({
        // CustomGroupId is the id OneTrust puts in OnetrustActiveGroups; OptanonGroupId is internal.
        id: String(group.CustomGroupId || ""),
        slug: slugify(group.GroupName),
        name: String(group.GroupName || ""),
        accepted: accepted.includes(String(group.CustomGroupId || "")),
      }))
      .filter((group) => group.id !== "");
  },
};

export default Api;
